/**
 * Safe Expression Parser for Calculated Columns
 * 
 * Parses and evaluates simple arithmetic expressions without using eval().
 * Supports:
 * - Column references: [column_name] or just column_name
 * - Arithmetic: + - * / %
 * - Comparison: > < >= <= == !=
 * - Logic: && ||
 * - Functions: abs, log, log10, round, floor, ceil, sqrt, pow, min, max
 * - Conditionals: if(condition, trueValue, falseValue)
 */

import {
    ParsedExpression,
    ExpressionOperator,
    ExpressionFunction,
    ALLOWED_FUNCTIONS,
    ALLOWED_OPERATORS
} from './dataPrepTypes';

// Re-export for use in other modules
export { ALLOWED_FUNCTIONS, ALLOWED_OPERATORS };

// ============================================================================
// Tokenizer
// ============================================================================

type TokenType = 'number' | 'string' | 'column' | 'operator' | 'function' | 'lparen' | 'rparen' | 'comma' | 'eof';

interface Token {
    type: TokenType;
    value: string | number;
    position: number;
}

class Tokenizer {
    private input: string;
    private pos: number = 0;
    private columnNames: Set<string>;

    constructor(input: string, columnNames: string[]) {
        this.input = input.trim();
        this.columnNames = new Set(columnNames.map(c => c.toLowerCase()));
    }

    private peek(): string {
        return this.input[this.pos] || '';
    }

    private advance(): string {
        return this.input[this.pos++] || '';
    }

    private skipWhitespace(): void {
        while (/\s/.test(this.peek())) this.advance();
    }

    private readNumber(): Token {
        const start = this.pos;
        let hasDecimal = false;

        while (/[\d.]/.test(this.peek())) {
            if (this.peek() === '.') {
                if (hasDecimal) break;
                hasDecimal = true;
            }
            this.advance();
        }

        return {
            type: 'number',
            value: parseFloat(this.input.slice(start, this.pos)),
            position: start
        };
    }

    private readString(): Token {
        const quote = this.advance(); // Skip opening quote
        const start = this.pos;

        while (this.peek() && this.peek() !== quote) {
            this.advance();
        }

        const value = this.input.slice(start, this.pos);
        this.advance(); // Skip closing quote

        return { type: 'string', value, position: start };
    }

    private readIdentifier(): Token {
        const start = this.pos;

        while (/[a-zA-Z0-9_.]/.test(this.peek())) {
            this.advance();
        }

        const value = this.input.slice(start, this.pos);
        const lowerValue = value.toLowerCase();

        // Check if it's a known function
        if (ALLOWED_FUNCTIONS.includes(lowerValue as ExpressionFunction)) {
            return { type: 'function', value: lowerValue, position: start };
        }

        // Otherwise it's a column reference
        return { type: 'column', value, position: start };
    }

    private readBracketedColumn(): Token {
        this.advance(); // Skip [
        const start = this.pos;

        while (this.peek() && this.peek() !== ']') {
            this.advance();
        }

        const value = this.input.slice(start, this.pos);
        this.advance(); // Skip ]

        return { type: 'column', value, position: start };
    }

    private readOperator(): Token | null {
        const start = this.pos;
        const char = this.peek();

        // Two-character operators
        const twoChar = this.input.slice(this.pos, this.pos + 2);
        if (['>=', '<=', '==', '!=', '&&', '||'].includes(twoChar)) {
            this.pos += 2;
            return { type: 'operator', value: twoChar, position: start };
        }

        // Single-character operators
        if (['+', '-', '*', '/', '%', '>', '<'].includes(char)) {
            this.advance();
            return { type: 'operator', value: char, position: start };
        }

        return null;
    }

    tokenize(): Token[] {
        const tokens: Token[] = [];

        while (this.pos < this.input.length) {
            this.skipWhitespace();
            if (this.pos >= this.input.length) break;

            const char = this.peek();

            if (/\d/.test(char)) {
                tokens.push(this.readNumber());
            } else if (char === '"' || char === "'") {
                tokens.push(this.readString());
            } else if (char === '[') {
                tokens.push(this.readBracketedColumn());
            } else if (char === '(') {
                tokens.push({ type: 'lparen', value: '(', position: this.pos });
                this.advance();
            } else if (char === ')') {
                tokens.push({ type: 'rparen', value: ')', position: this.pos });
                this.advance();
            } else if (char === ',') {
                tokens.push({ type: 'comma', value: ',', position: this.pos });
                this.advance();
            } else if (/[a-zA-Z_]/.test(char)) {
                tokens.push(this.readIdentifier());
            } else {
                const op = this.readOperator();
                if (op) {
                    tokens.push(op);
                } else {
                    throw new Error(`Unexpected character '${char}' at position ${this.pos}`);
                }
            }
        }

        tokens.push({ type: 'eof', value: '', position: this.pos });
        return tokens;
    }
}

// ============================================================================
// Parser (Recursive Descent)
// ============================================================================

class ExpressionParser {
    private tokens: Token[];
    private pos: number = 0;

    constructor(tokens: Token[]) {
        this.tokens = tokens;
    }

    private peek(): Token {
        return this.tokens[this.pos] || { type: 'eof', value: '', position: -1 };
    }

    private advance(): Token {
        return this.tokens[this.pos++];
    }

    private expect(type: TokenType, msg?: string): Token {
        const token = this.peek();
        if (token.type !== type) {
            throw new Error(msg || `Expected ${type} but got ${token.type} at position ${token.position}`);
        }
        return this.advance();
    }

    parse(): ParsedExpression {
        const expr = this.parseOr();
        if (this.peek().type !== 'eof') {
            throw new Error(`Unexpected token '${this.peek().value}' at position ${this.peek().position}`);
        }
        return expr;
    }

    // Precedence: || < && < comparison < additive < multiplicative < unary < primary
    private parseOr(): ParsedExpression {
        let left = this.parseAnd();

        while (this.peek().type === 'operator' && this.peek().value === '||') {
            const op = this.advance().value as ExpressionOperator;
            const right = this.parseAnd();
            left = { type: 'binary', operator: op, left, right };
        }

        return left;
    }

    private parseAnd(): ParsedExpression {
        let left = this.parseComparison();

        while (this.peek().type === 'operator' && this.peek().value === '&&') {
            const op = this.advance().value as ExpressionOperator;
            const right = this.parseComparison();
            left = { type: 'binary', operator: op, left, right };
        }

        return left;
    }

    private parseComparison(): ParsedExpression {
        let left = this.parseAdditive();

        const compOps = ['>', '<', '>=', '<=', '==', '!='];
        while (this.peek().type === 'operator' && compOps.includes(this.peek().value as string)) {
            const op = this.advance().value as ExpressionOperator;
            const right = this.parseAdditive();
            left = { type: 'binary', operator: op, left, right };
        }

        return left;
    }

    private parseAdditive(): ParsedExpression {
        let left = this.parseMultiplicative();

        while (this.peek().type === 'operator' && ['+', '-'].includes(this.peek().value as string)) {
            const op = this.advance().value as ExpressionOperator;
            const right = this.parseMultiplicative();
            left = { type: 'binary', operator: op, left, right };
        }

        return left;
    }

    private parseMultiplicative(): ParsedExpression {
        let left = this.parseUnary();

        while (this.peek().type === 'operator' && ['*', '/', '%'].includes(this.peek().value as string)) {
            const op = this.advance().value as ExpressionOperator;
            const right = this.parseUnary();
            left = { type: 'binary', operator: op, left, right };
        }

        return left;
    }

    private parseUnary(): ParsedExpression {
        if (this.peek().type === 'operator' && this.peek().value === '-') {
            this.advance();
            const operand = this.parseUnary();
            return {
                type: 'binary',
                operator: '*',
                left: { type: 'literal', value: -1 },
                right: operand
            };
        }
        return this.parsePrimary();
    }

    private parsePrimary(): ParsedExpression {
        const token = this.peek();

        if (token.type === 'number') {
            this.advance();
            return { type: 'literal', value: token.value as number };
        }

        if (token.type === 'string') {
            this.advance();
            return { type: 'literal', value: token.value as string };
        }

        if (token.type === 'column') {
            this.advance();
            return { type: 'column', columnName: token.value as string };
        }

        if (token.type === 'function') {
            return this.parseFunction();
        }

        if (token.type === 'lparen') {
            this.advance();
            const expr = this.parseOr();
            this.expect('rparen', 'Expected closing parenthesis');
            return expr;
        }

        throw new Error(`Unexpected token '${token.value}' at position ${token.position}`);
    }

    private parseFunction(): ParsedExpression {
        const funcToken = this.advance();
        const funcName = funcToken.value as ExpressionFunction;

        this.expect('lparen', `Expected '(' after function ${funcName}`);

        const args: ParsedExpression[] = [];

        if (this.peek().type !== 'rparen') {
            args.push(this.parseOr());

            while (this.peek().type === 'comma') {
                this.advance();
                args.push(this.parseOr());
            }
        }

        this.expect('rparen', `Expected ')' after function arguments`);

        // Special handling for 'if' function
        if (funcName === 'if') {
            if (args.length !== 3) {
                throw new Error(`if() requires exactly 3 arguments: condition, trueValue, falseValue`);
            }
            return {
                type: 'conditional',
                condition: args[0],
                trueBranch: args[1],
                falseBranch: args[2]
            };
        }

        return { type: 'function', functionName: funcName, args };
    }
}

// ============================================================================
// Evaluator
// ============================================================================

export function evaluateExpression(expr: ParsedExpression, row: Record<string, any>): number | string | boolean | null {
    switch (expr.type) {
        case 'literal':
            return expr.value!;

        case 'column': {
            const val = row[expr.columnName!];
            if (val === undefined || val === null || val === '') return null;
            const num = parseFloat(val);
            return isNaN(num) ? val : num;
        }

        case 'binary': {
            const left = evaluateExpression(expr.left!, row);
            const right = evaluateExpression(expr.right!, row);

            // Handle nulls
            if (left === null || right === null) return null;

            const leftNum = typeof left === 'number' ? left : parseFloat(left as string);
            const rightNum = typeof right === 'number' ? right : parseFloat(right as string);

            switch (expr.operator) {
                case '+': return isNaN(leftNum) || isNaN(rightNum) ? `${left}${right}` : leftNum + rightNum;
                case '-': return leftNum - rightNum;
                case '*': return leftNum * rightNum;
                case '/': return rightNum === 0 ? null : leftNum / rightNum;
                case '%': return rightNum === 0 ? null : leftNum % rightNum;
                case '>': return leftNum > rightNum;
                case '<': return leftNum < rightNum;
                case '>=': return leftNum >= rightNum;
                case '<=': return leftNum <= rightNum;
                case '==': return left == right;
                case '!=': return left != right;
                case '&&': return Boolean(left) && Boolean(right);
                case '||': return Boolean(left) || Boolean(right);
                default: return null;
            }
        }

        case 'function': {
            const args = expr.args!.map(a => evaluateExpression(a, row));

            // Check for null args
            if (args.some(a => a === null)) return null;

            const numArgs = args.map(a => typeof a === 'number' ? a : parseFloat(a as string));

            switch (expr.functionName) {
                case 'abs': return Math.abs(numArgs[0]);
                case 'log': return numArgs[0] <= 0 ? null : Math.log(numArgs[0]);
                case 'log10': return numArgs[0] <= 0 ? null : Math.log10(numArgs[0]);
                case 'round': return Math.round(numArgs[0]);
                case 'floor': return Math.floor(numArgs[0]);
                case 'ceil': return Math.ceil(numArgs[0]);
                case 'sqrt': return numArgs[0] < 0 ? null : Math.sqrt(numArgs[0]);
                case 'pow': return Math.pow(numArgs[0], numArgs[1] ?? 2);
                case 'min': return Math.min(...numArgs);
                case 'max': return Math.max(...numArgs);
                default: return null;
            }
        }

        case 'conditional': {
            const condition = evaluateExpression(expr.condition!, row);
            if (condition) {
                return evaluateExpression(expr.trueBranch!, row);
            } else {
                return evaluateExpression(expr.falseBranch!, row);
            }
        }

        default:
            return null;
    }
}

// ============================================================================
// Public API
// ============================================================================

export interface ParseResult {
    success: boolean;
    expression?: ParsedExpression;
    error?: string;
    referencedColumns: string[];
}

export function parseExpression(formula: string, columnNames: string[]): ParseResult {
    try {
        const tokenizer = new Tokenizer(formula, columnNames);
        const tokens = tokenizer.tokenize();

        const parser = new ExpressionParser(tokens);
        const expression = parser.parse();

        // Extract referenced columns
        const referencedColumns: string[] = [];
        const extractColumns = (expr: ParsedExpression) => {
            if (expr.type === 'column' && expr.columnName) {
                referencedColumns.push(expr.columnName);
            }
            if (expr.left) extractColumns(expr.left);
            if (expr.right) extractColumns(expr.right);
            if (expr.args) expr.args.forEach(extractColumns);
            if (expr.condition) extractColumns(expr.condition);
            if (expr.trueBranch) extractColumns(expr.trueBranch);
            if (expr.falseBranch) extractColumns(expr.falseBranch);
        };
        extractColumns(expression);

        return {
            success: true,
            expression,
            referencedColumns: [...new Set(referencedColumns)]
        };
    } catch (e) {
        return {
            success: false,
            error: e instanceof Error ? e.message : 'Unknown parse error',
            referencedColumns: []
        };
    }
}

export function validateFormula(formula: string, columnNames: string[]): { valid: boolean; error?: string } {
    const result = parseExpression(formula, columnNames);
    if (!result.success) {
        return { valid: false, error: result.error };
    }

    // Check that all referenced columns exist
    const lowerColumns = new Set(columnNames.map(c => c.toLowerCase()));
    for (const col of result.referencedColumns) {
        if (!lowerColumns.has(col.toLowerCase()) && !columnNames.includes(col)) {
            return { valid: false, error: `Unknown column: ${col}` };
        }
    }

    return { valid: true };
}

export function evaluateFormula(
    formula: string,
    row: Record<string, any>,
    columnNames: string[]
): { value: number | string | boolean | null; error?: string } {
    const parseResult = parseExpression(formula, columnNames);
    if (!parseResult.success) {
        return { value: null, error: parseResult.error };
    }

    try {
        const value = evaluateExpression(parseResult.expression!, row);
        return { value };
    } catch (e) {
        return { value: null, error: e instanceof Error ? e.message : 'Evaluation error' };
    }
}
